/**
 * Route → human title, resolved from the navigation config.
 *
 * The portal already knows what every page is called — `navigationConfig` carries a
 * label for each path so the nav can render it. Phase 8 W2 reuses that instead of
 * de-slugging the URL, so `/admin/trading/broker-market-accesses` renders as
 * "Broker Market Access" (what the nav calls it) rather than
 * "Broker Market Accesses" (what a slug-splitter would invent).
 *
 * Falls back to a title-cased last segment when a path is not in the nav — honest,
 * and visible enough that a missing nav entry gets noticed.
 */
import { navigationConfig, type NavigationItem } from './navigationConfig';

function walk(items: NavigationItem[], into: Map<string, string>): void {
  for (const item of items) {
    if (item.path && !into.has(item.path)) into.set(item.path, item.label);
    if (item.children?.length) walk(item.children, into);
  }
}

let cache: Map<string, string> | null = null;

function titleMap(): Map<string, string> {
  if (!cache) {
    cache = new Map<string, string>();
    for (const section of navigationConfig) walk(section.children, cache);
  }
  return cache;
}

/** Title-case a URL slug: 'broker-market-accesses' → 'Broker Market Accesses'. */
function fromSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => (w.length <= 3 && w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * The display title for a pathname. Exact nav match first, then the nearest parent
 * (so detail routes like `/admin/x/y/:id` inherit their list page's name), then the
 * slug fallback.
 */
export function titleForPath(pathname: string): string {
  const map = titleMap();
  const exact = map.get(pathname);
  if (exact) return exact;

  const parts = pathname.split('/').filter(Boolean);
  for (let i = parts.length - 1; i > 0; i -= 1) {
    const parent = `/${parts.slice(0, i).join('/')}`;
    const hit = map.get(parent);
    if (hit) return hit;
  }
  return parts.length ? fromSlug(parts[parts.length - 1]) : 'Qiyas';
}
